import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../lib/config';
import {
  BellIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const NotificationManagement = ({ onBack }) => {
  const [notifications, setNotifications] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    target_audience: 'all',
    scheduled_time: '',
    is_active: true
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('通知の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotification = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.post(`${API_BASE}/admin/notifications`, {
        ...formData,
        scheduled_time: formData.scheduled_time ? new Date(formData.scheduled_time).toISOString() : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('通知を作成しました');
      setShowCreateForm(false);
      resetForm();
      fetchNotifications();
    } catch (error) {
      toast.error('通知の作成に失敗しました');
    }
  };

  const handleUpdateNotification = async () => {
    if (!editingNotification) return;
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.put(`${API_BASE}/admin/notifications/${editingNotification.id}`, {
        ...formData,
        scheduled_time: formData.scheduled_time ? new Date(formData.scheduled_time).toISOString() : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('通知を更新しました');
      setEditingNotification(null);
      resetForm();
      fetchNotifications();
    } catch (error) {
      toast.error('通知の更新に失敗しました');
    }
  };

  const handleDeleteNotification = async (id) => {
    if (!window.confirm('この通知を削除しますか？')) return;

    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.delete(`${API_BASE}/admin/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('通知を削除しました');
      fetchNotifications();
    } catch (error) {
      toast.error('通知の削除に失敗しました');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      target_audience: 'all',
      scheduled_time: '',
      is_active: true
    });
  };

  const startEdit = (notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      target_audience: notification.target_audience,
      scheduled_time: notification.scheduled_time ? notification.scheduled_time.slice(0, 16) : '',
      is_active: notification.is_active
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingNotification) {
      handleUpdateNotification();
    } else {
      handleCreateNotification();
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'info':
        return <CheckCircleIcon className="w-5 h-5 text-blue-500" />;
      case 'warning':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'important':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <BellIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      info: { class: 'bg-blue-100 text-blue-800', label: '情報' },
      warning: { class: 'bg-yellow-100 text-yellow-800', label: '注意' },
      important: { class: 'bg-red-100 text-red-800', label: '重要' }
    };
    const badge = badges[type] || badges.info;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const getAudienceLabel = (audience) => {
    const labels = {
      all: 'すべてのユーザー',
      workers: 'ワーカー',
      employers: '事業者'
    };
    return labels[audience] || audience;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">通知管理</h2>
          <p className="text-gray-600 mt-1">ユーザーへのプッシュ通知とお知らせを管理します</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          通知作成
        </button>
      </div>

      {/* 通知作成・編集フォーム */}
      {(showCreateForm || editingNotification) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">
            {editingNotification ? '通知編集' : '通知作成'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  タイトル *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="通知のタイトルを入力"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  通知タイプ
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="info">情報</option>
                  <option value="warning">注意</option>
                  <option value="important">重要</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                メッセージ *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                placeholder="通知メッセージを入力"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  対象ユーザー
                </label>
                <select
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">すべてのユーザー</option>
                  <option value="workers">ワーカー</option>
                  <option value="employers">事業者</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  配信予定時刻（任意）
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                通知を有効にする
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingNotification ? '更新' : '作成'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingNotification(null);
                  resetForm();
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 通知一覧 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">通知一覧</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">読み込み中...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    通知情報
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    対象・配信
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    統計
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getTypeIcon(notification.type)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {notification.message}
                          </div>
                          <div className="mt-1">
                            {getTypeBadge(notification.type)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900 flex items-center">
                          <UserGroupIcon className="w-4 h-4 mr-1" />
                          {getAudienceLabel(notification.target_audience)}
                        </div>
                        {notification.scheduled_time ? (
                          <div className="text-gray-500 flex items-center mt-1">
                            <CalendarIcon className="w-4 h-4 mr-1" />
                            予定: {formatDateTime(notification.scheduled_time)}
                          </div>
                        ) : (
                          <div className="text-gray-500">即時配信</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">配信: {(notification.sent_count || 0).toLocaleString()}件</div>
                        <div className="text-gray-500">既読: {(notification.read_count || 0).toLocaleString()}件</div>
                        <div className="text-gray-500 text-xs">
                          既読率: {notification.sent_count > 0 ? ((notification.read_count / notification.sent_count) * 100).toFixed(1) : 0}%
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${notification.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {notification.is_active ? '有効' : '無効'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(notification)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteNotification(notification.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagement;